import { useResponsiveWidth } from '@/hooks/useResponsiveWidth';
import { scaleLinear, scaleUtc } from '@visx/scale';
import { useRef } from 'react';

import { cn } from '@simplywallst/ui-core/next';
import { XAxis } from '../XAxis';
import { YAxis } from '../YAxis';

import type { Period } from '../../types';
import {
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_PADDING_BOTTOM,
  CHART_PADDING_RIGHT,
} from './constants';

type Props = {
  period: Period;
  startTimestamp: number;
  endTimestamp: number;
};

export const RestrictedChart = ({
  period,
  startTimestamp,
  endTimestamp,
}: Props) => {
  const { current: xScale } = useRef(scaleUtc<number>());
  const { current: yScale } = useRef(scaleLinear<number>());

  const { ref, width: chartWidth } = useResponsiveWidth();

  xScale.range([CHART_PADDING, chartWidth - CHART_PADDING_RIGHT]);
  xScale.domain([startTimestamp, endTimestamp]);

  yScale.range([CHART_HEIGHT - CHART_PADDING_BOTTOM, CHART_PADDING]);
  yScale.domain([0, 0]);

  return (
    <div
      ref={ref}
      data-testid="restricted-portfolio-performance-chart"
      className={cn(
        'grid',
        'grid-rows-[var(--portfolio-performance-chart-height)]'
      )}
      style={
        {
          '--portfolio-performance-chart-height': `${CHART_HEIGHT}px`,
        } as React.CSSProperties
      }
    >
      <div className="relative">
        <svg
          y="0"
          x="0"
          width={chartWidth}
          height={CHART_HEIGHT}
          role="application"
          aria-label="Portfolio Performance Chart"
          className="absolute left-0 top-0 animate-fade-in"
        >
          <XAxis xScale={xScale} yScale={yScale} period={period} />
          <YAxis yScale={yScale} width={chartWidth} />
        </svg>
      </div>
    </div>
  );
};
